import React, { useEffect, useRef, useState } from 'react';

import styles from './DogCardGeneralOverview.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../../../../Auth';
import { useAxios } from '../../../../../../AxiosHandler';
import Card from '../../../../../Shared/Card/Card';
import StatusDot from '../../../../../Shared/StatusDot/StatusDot';
import { general } from '../../../../../../general';
import TextLink from '../../../../../Shared/TextLink/TextLink';

function DogCardGeneralOverview(props: DogCardGeneralOverviewProps) {
    const t = useTranslate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;

    const [showOwnerInfo, setShowOwnerInfo] = useState<boolean>(false);
    const [ownerData, setOwnerData] = useState<any>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [hittaalghundExists, setHittaalghundExists] = useState<boolean>(false);
    const [photoKey, setPhotoKey] = useState<number>(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    function uploadPhoto(file: File) {
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = 300;
                canvas.height = 300;
                const ctx = canvas.getContext('2d')!;
                const size = Math.min(img.width, img.height);
                const sx = (img.width - size) / 2;
                const sy = (img.height - size) / 2;
                ctx.drawImage(img, sx, sy, size, size, 0, 0, 300, 300);
                const base64 = canvas.toDataURL('image/jpeg', 0.85);
                axios.single({
                    method: 'post',
                    url: `/api/dogs/photo/${props.selectedDog?.ANIMAL_ID}`,
                    data: { photo: base64 },
                    responseType: 'json',
                    headers: {
                        'x-access-token': auth.user['token'],
                        'selected-country': auth.selectedCountry || 'all',
                    },
                }).then(() => {
                    setPhotoKey(k => k + 1);
                }).catch((err: any) => {
                    console.log(err);
                });
            };
            img.src = ev.target?.result as string;
        };
        reader.readAsDataURL(file);
    }

    const hittaalghundEnabled = auth.user?.userdata?.COUNCIL_ACCESS?.find((c: any) => c.BREED_ID == auth.selectedBreed)?.HITTAALGHUND_ENABLED == 1;

    useEffect(() => {
        if (!hittaalghundEnabled || !props.selectedDog?.REGNO) {
            setHittaalghundExists(false);
            return;
        }
        axios.single({
            method: 'get',
            url: '/api/dogs/check/hittaalghund/' + encodeURIComponent(props.selectedDog.REGNO),
            responseType: 'json',
        }).then((res: any) => {
            setHittaalghundExists(res.data?.exists === true);
        }).catch(() => {
            setHittaalghundExists(false);
        });
    }, [props.selectedDog?.REGNO, hittaalghundEnabled]);

    function fetchOwnerInfo() {
        if (auth.selectedBreed == undefined) return;
        setLoading(true);
        axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + props.dogId + "/owner",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            setOwnerData(res.data?.[0]);
            setLoading(false);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    return (
        <div className={styles.generaloverview}>
            <div className={styles.dogcardwrapper}>
            <div
                className={styles.dogavatar}
                onClick={() => fileInputRef.current?.click()}
                title={t("dog.details.general.changephoto")}
            >
                <span className={styles.dogavatarplaceholder}>
                    {(props.selectedDog?.NAME ?? "?")[0]}
                </span>
                {props.selectedDog?.ANIMAL_ID && (
                    <img
                        key={photoKey}
                        src={`/api/dogs/photo/${props.selectedDog.ANIMAL_ID}?v=${photoKey}`}
                        alt=""
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                )}
                <div className={styles.dogavataroverlay}>
                    <span>&#9998;</span>
                </div>
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadPhoto(file);
                    e.target.value = '';
                }}
            />
            <Card
                title={props.title || "General Overview"}
                children={<div>
                    <hr/>
                    <table className={styles.generaltable}>
                        <tbody>
                            <tr>
                                <td><strong>{t("dog.details.general.birthdate")}:</strong></td>
                                <td><span style={{marginRight: "6px"}}>{general.dateToString(new Date(props.selectedDog?.DATE_BIRTH))}</span>{}</td>
                            </tr>
                            <tr>
                                <td><strong>{t("dog.details.general.regnumber")}:</strong></td>
                                <td><span style={{marginRight: "6px"}}>{props.selectedDog?.REGNO}</span>{}</td>
                            </tr>
                            <tr>
                                <td><strong>{t("dog.details.general.chip")}:</strong></td>
                                <td><span style={{marginRight: "6px"}}>{props.selectedDog?.CHIP}</span>{}</td>
                            </tr>
                            <tr>
                                <td><strong>{t("dog.details.general.postcode")}:</strong></td>
                                <td><span style={{marginRight: "6px"}}>{props.selectedDog?.POSTAL_CODE}</span>{}</td>
                            </tr>
                            <tr>
                                <td><strong>{t("dog.details.general.color")}:</strong></td>
                                <td><span style={{marginRight: "6px"}}>{props.selectedDog?.COLOR}</span>{}</td>
                            </tr>
                        </tbody>
                    </table>
                    <hr/>
                    { showOwnerInfo ?
                        loading == false ?
                            ownerData != undefined ?
                                <table className={styles.generaltable}>
                                    <tbody>
                                        <tr>
                                            <td><strong>{t("dog.details.owner.membernumber")}:</strong></td>
                                            <td><span style={{marginRight: "6px"}}>{ownerData?.PERSON_MEMBER_NO}</span></td>
                                        </tr>
                                        <tr>
                                            <td><strong>{t("dog.details.owner.name")}:</strong></td>
                                            <td><span style={{marginRight: "6px"}}>{ownerData?.PERSON_LAST_NAME}, {ownerData?.PERSON_FIRST_NAME}</span></td>
                                        </tr>
                                        <tr>
                                            <td><strong>{t("dog.details.owner.email")}:</strong></td>
                                            <td><span style={{marginRight: "6px"}}>{ownerData?.EMAIL_VALUE}</span></td>
                                        </tr>
                                        <tr>
                                            <td><strong>{t("dog.details.owner.phone")}:</strong></td>
                                            <td><span style={{marginRight: "6px"}}>{ownerData?.PHONE_VALUE}</span></td>
                                        </tr>
                                        {ownerData?.ADDRESS_LINE1 &&
                                            <tr>
                                                <td><strong>{t("dog.details.owner.addressln1")}:</strong></td>
                                                <td><span style={{marginRight: "6px"}}>{ownerData?.ADDRESS_LINE1}</span></td>
                                            </tr>
                                        }
                                        {ownerData?.ADDRESS_LINE2 &&
                                            <tr>
                                                <td><strong>{t("dog.details.owner.addressln2")}:</strong></td>
                                                <td><span style={{marginRight: "6px"}}>{ownerData?.ADDRESS_LINE2}</span></td>
                                            </tr>
                                        }
                                        {ownerData?.ADDRESS_LINE3 &&
                                            <tr>
                                                <td><strong>{t("dog.details.owner.addressln3")}:</strong></td>
                                                <td><span style={{marginRight: "6px"}}>{ownerData?.ADDRESS_LINE3}</span></td>
                                            </tr>
                                        }
                                        <tr>
                                            <td><strong>{t("dog.details.owner.postalarea")}:</strong></td>
                                            <td><span style={{marginRight: "6px"}}>{ownerData?.ADDRESS_ZIP}, {ownerData?.ADDRESS_CITY} {t("general.countrycodes." + ownerData?.ADDRESS_COUNTRY_CODE)}</span></td>
                                        </tr>
                                    </tbody>
                                </table>
                            : <div>{t("dog.details.not_found")}</div>
                        : <div>{t("dog.details.loading")}</div>
                    : null }
                    <div className={styles.linkscontainer}>
                        { general.getBreedAccess(auth.user, auth.selectedBreed) == "admin" ||
                        general.getBreedAccess(auth.user, auth.selectedBreed) == "user" ?
                            (!showOwnerInfo ?
                                <TextLink
                                    onClick={() => {
                                        setShowOwnerInfo(true);
                                        fetchOwnerInfo();
                                    }}
                                >{t("dog.details.general.getownerinfo")}</TextLink>
                            :
                                <TextLink
                                    onClick={() => {
                                        setShowOwnerInfo(false);
                                        setOwnerData(undefined);
                                    }}
                                >{t("dog.details.general.hideownerinfo")}</TextLink>
                            )
                        : null }
                        <TextLink
                            onClick={() => {
                                window.open("https://www.dogweb.no/hundedatabase/dog?id=" + props.selectedDog?.REGNO, "_blank");
                            }}
                        >Se i Dogweb</TextLink>
                        {hittaalghundExists && (
                            <TextLink
                                onClick={() => {
                                    window.open("https://www.hittaalghund.se/dog.asp?Regnummer=" + props.selectedDog?.REGNO, "_blank");
                                }}
                            >Se i Hitta Alghund</TextLink>
                        )}
                    </div>
                </div>}
            ></Card>            </div>        </div>
    );
}

class DogCardGeneralOverviewProps {
    title?: string;
    selectedDog!: any | undefined;
    dogId!: string | undefined;
}

export default DogCardGeneralOverview;